import { useEffect, useState } from "react";
import { DepartmentFilter, useDepartments } from "../../hooks/useDepartments";
import { PlanLocked } from "../../components/PlanLocked";
import { useEntitlements } from "../../hooks/useEntitlements";
import { PieceRateReport } from "../../components/PieceRateReport";
import { PieceReviews } from "../../components/PieceReviews";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { paths, type PayPeriod, type PayrollRun, type TimeEntry } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { useMemberNames } from "../../hooks/useMemberNames";

type EntryRow = TimeEntry & { id: string };
type PeriodRow = PayPeriod & { id: string };
type RunRow = PayrollRun & { id: string; csvContent?: string };

function hours(mins: number) {
  return (mins / 60).toFixed(2);
}
function fmtDay(ms: number) {
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function TimesheetsAdmin() {
  const { tenantId, role } = useAuth();
  const { hasFeature } = useEntitlements();
  // Managers look at their own department's people (remembered per person).
  const dept = useDepartments();
  const isAdmin = role === "owner" || role === "admin";
  const [pending, setPending] = useState<EntryRow[]>([]);
  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [periodForm, setPeriodForm] = useState({ start: "", end: "" });
  const memberName = useMemberNames(tenantId);

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(
        query(
          collection(db, paths.tenantTimeEntries(tenantId)),
          where("status", "==", "submitted"),
          orderBy("clockInAt", "desc"),
        ),
        (snap) => setPending(snap.docs.map((d) => ({ id: d.id, ...(d.data() as TimeEntry) }))),
      ),
      onSnapshot(query(collection(db, paths.tenantPayPeriods(tenantId)), orderBy("startAt", "desc")), (snap) =>
        setPeriods(snap.docs.map((d) => ({ id: d.id, ...(d.data() as PayPeriod) }))),
      ),
    ];
    if (isAdmin) {
      subs.push(
        onSnapshot(query(collection(db, paths.tenantPayrollRuns(tenantId)), orderBy("createdAt", "desc")), (snap) =>
          setRuns(snap.docs.map((d) => ({ ...(d.data() as PayrollRun & { csvContent?: string }), id: d.id }))),
        ),
      );
    }
    return () => subs.forEach((u) => u());
  }, [tenantId, isAdmin]);

  async function review(entryId: string, decision: "approved" | "rejected") {
    if (!tenantId) return;
    setMessage("");
    try {
      await call("reviewTimeEntry", { tenantId, entryId, decision });
    } catch (err) {
      setMessage((err as Error).message);
    }
  }

  async function createPeriod() {
    if (!tenantId) return;
    setBusy(true);
    setMessage("");
    try {
      await call("createPayPeriod", {
        tenantId,
        startAt: new Date(`${periodForm.start}T00:00`).getTime(),
        endAt: new Date(`${periodForm.end}T23:59`).getTime() + 60000,
      });
      setPeriodForm({ start: "", end: "" });
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function prepareRun(payPeriodId: string) {
    if (!tenantId) return;
    setBusy(true);
    setMessage("");
    try {
      const res = await call<{ people: number }>("preparePayrollRun", {
        tenantId,
        payPeriodId,
        providerSlug: "payroll-csv",
      });
      setMessage(`Payroll run prepared for ${res.people} people.`);
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function downloadCsv(run: RunRow) {
    const blob = new Blob([run.csvContent ?? ""], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `payroll-${fmtDay(run.createdAt).replace(" ", "-")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const shownPending = pending.filter((e) => dept.personMatches(e.uid));

  function entryMinutes(e: EntryRow) {
    if (!e.clockOutAt) return 0;
    return Math.max(0, Math.round((e.clockOutAt - e.clockInAt) / 60000) - e.breakMinutes);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-bold">Timesheets</h1>
        <DepartmentFilter dept={dept} />
      </div>
      {tenantId && hasFeature("pieceRate") ? <PieceReviews tenantId={tenantId} people={dept.current === "all" ? undefined : (uid) => dept.personMatches(uid)} /> : null}
      {tenantId && hasFeature("pieceRate") ? <PieceRateReport tenantId={tenantId} people={dept.current === "all" ? undefined : (uid) => dept.personMatches(uid)} /> : null}
      {message ? <p className="text-xs text-ink">{message}</p> : null}

      <Card className="p-0">
        <CardTitle className="px-4 pt-3">Waiting for approval ({shownPending.length})</CardTitle>
        {shownPending.map((e) => (
          <div key={e.id} className="flex items-center justify-between gap-3 border-b border-steel/10 px-4 py-2 last:border-0">
            <div className="min-w-0">
              <div className="truncate text-sm text-ink">{e.displayName || memberName(e.uid)}</div>
              <div className="text-xs text-steel">
                {fmtDay(e.clockInAt)} · {hours(entryMinutes(e))}h
                {e.breakMinutes ? ` (break ${e.breakMinutes}m)` : ""}
                {e.clockInGeo ? " · GPS ✓" : " · no GPS"}
              </div>
              {e.note ? <div className="truncate text-xs text-steel">"{e.note}"</div> : null}
            </div>
            <div className="flex shrink-0 gap-2">
              <Button className="px-3 py-1 text-xs" onClick={() => void review(e.id, "approved")}>
                Approve
              </Button>
              <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => void review(e.id, "rejected")}>
                Reject
              </Button>
            </div>
          </div>
        ))}
        {shownPending.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-steel">Nothing waiting — approvals happen as they come in.</div>
        ) : null}
      </Card>

      {isAdmin ? (
        <>
          {!hasFeature("payroll") ? <PlanLocked feature="payroll" /> : null}
          <Card className={hasFeature("payroll") ? undefined : "hidden"}>
            <CardTitle>Pay periods</CardTitle>
            <div className="mb-3 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-2">
              <div className="min-w-0">
                <Label>Start</Label>
                <Input
                  type="date"
                  className="min-w-0"
                  value={periodForm.start}
                  onChange={(e) => setPeriodForm({ ...periodForm, start: e.target.value })}
                />
              </div>
              <div className="min-w-0">
                <Label>End</Label>
                <Input
                  type="date"
                  className="min-w-0"
                  value={periodForm.end}
                  onChange={(e) => setPeriodForm({ ...periodForm, end: e.target.value })}
                />
              </div>
              <Button onClick={createPeriod} busy={busy} disabled={!periodForm.start || !periodForm.end}>
                Add period
              </Button>
            </div>
            {periods.map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-steel/10 py-2 last:border-0">
                <div className="text-sm text-ink">
                  {fmtDay(p.startAt)} – {fmtDay(p.endAt)}
                  <span className="ml-2 rounded-full bg-steel/15 px-2 py-0.5 text-xs">{p.status}</span>
                </div>
                {p.status === "open" ? (
                  <Button className="px-3 py-1 text-xs" busy={busy} onClick={() => void prepareRun(p.id)}>
                    Run payroll
                  </Button>
                ) : null}
              </div>
            ))}
            {periods.length === 0 ? <p className="py-2 text-sm text-steel">No pay periods yet.</p> : null}
          </Card>

          <Card>
            <CardTitle>Payroll runs</CardTitle>
            {runs.map((r) => (
              <div key={r.id} className="flex items-center justify-between border-b border-steel/10 py-2 last:border-0">
                <div className="text-sm text-ink">
                  {fmtDay(r.createdAt)} · {r.lines.length} people · {hours(r.totalRegularMinutes)}h reg
                  {r.totalOvertimeMinutes > 0 ? ` + ${hours(r.totalOvertimeMinutes)}h OT` : ""}
                  {r.totalGrossCents > 0 ? ` · $${(r.totalGrossCents / 100).toFixed(2)} hourly` : ""}
                  {(r.totalPieceCents ?? 0) > 0 ? ` · $${((r.totalPieceCents ?? 0) / 100).toFixed(2)} piece` : ""}
                  {(r.totalCommissionCents ?? 0) > 0 ? ` · $${((r.totalCommissionCents ?? 0) / 100).toFixed(2)} commission` : ""}
                </div>
                <Button variant="secondary" className="px-3 py-1 text-xs" onClick={() => downloadCsv(r)}>
                  Download CSV
                </Button>
              </div>
            ))}
            {runs.length === 0 ? (
              <p className="py-2 text-sm text-steel">
                No runs yet. Approve entries, add a pay period, press "Run payroll" — no cave required.
              </p>
            ) : null}
          </Card>
        </>
      ) : null}
    </div>
  );
}
